import classNames from "classnames";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSortBy, useTable, usePagination, Row } from 'react-table';
import { reportTimeFormat } from "../constants";
import { Project, PunchEntry } from "../service/backend-service";
import { TimeUtils } from "../util/time-utils";
import PunchEdit from "./punch-edit";

interface PunchData {
  startEnd: string,
  project: Project,
  sprint: number,
  description: string,
  timeWorked: string
  editLink: JSX.Element
}

function ReportsTable({ punches, onItemChange }: { 
  punches: Array<PunchEntry> 
  onItemChange: (punch: PunchEntry) => void
}) {
  const centeredColumns = ['sprint', 'timeWorked', 'editLink'];
  const pageSizes = [10, 20, 30, 40, 50];

  //Used to trigger tableData memo to refresh when records is changed via edit
  const [changeCounter, setChangeCounter] = useState(() => 1);

  const projectSort = useMemo(
    () => (rowA: Row<PunchData>, rowB: Row<PunchData>, id: string) => {
      const a = rowA.values[id].name.toLowerCase();
      const b = rowB.values[id].name.toLowerCase();
      if (a > b) return 1;
      if (a < b) return -1;
      return 0;
    },
    []
  );

  const columns = useMemo(() => [
    {
      Header: 'Punch Start - Punch End',
      accessor: 'startEnd' as keyof PunchData,
      width: '40%',
    },
    {
      Header: 'Project',
      accessor: 'project' as keyof PunchData,
      width: '11%',
      Cell: ({ value }: { value: Project }) => {
        return <>
          <i className={classNames({
            'fa-fw fa-heart text-danger': true,
            'fas': value.favorite,
            'far': !value.favorite
          })} />
          <i className={classNames({
            'fa-fw fa-lightbulb text-warning': true,
            'fas': value.active,
            'far': !value.active
          })} />
          <span className={"ms-2 badge bg-" + value.color}>
            {value.name}
          </span>
        </>;
      },
      sortType: projectSort
    },
    {
      Header: 'Sprint',
      accessor: 'sprint' as keyof PunchData,
      width: '7%'
    },
    {
      Header: 'Description',
      accessor: 'description' as keyof PunchData,
      width: '30%'
    },
    {
      Header: 'Time Worked',
      accessor: 'timeWorked' as keyof PunchData,
      width: '10%'
    },
    {
      Header: '',
      accessor: 'editLink' as keyof PunchData,
      width: '2%',
      disableSortBy: true
      
    },
  ], [projectSort]);

  const tableData = useMemo(() => punches.map(punch => {
    let refreshUpdatedItem = (item: PunchEntry) => {
      onItemChange(item);
      setChangeCounter(changeCounter + 1);
    }

    return {
      startEnd: moment(punch.start).format(reportTimeFormat) + ' - ' + (!!!punch.end ? 'Running' : moment(punch.end).format(reportTimeFormat)),
      project: punch.project,
      sprint: punch.sprintId,
      description: punch.workDescription,
      timeWorked: TimeUtils.timeWorked(punch),
      editLink: punch.project.active 
                  ? <PunchEdit extraClasses={'text-center'} punchEntry={punch} onSave={refreshUpdatedItem}/>
                  : <i title="Cannot edit inactive project" className={classNames("fas fa-fw fa-pen-to-square text-muted text-center")}/>
    }
  }), [punches, changeCounter, onItemChange]);

  const {
    headers,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns: columns,
      data: tableData,
      disableMultiSort: true,
      initialState: {
        pageSize: 20,
        sortBy: [
          {
            id: 'startEnd'
          }
        ]
      },
    },
    useSortBy,
    usePagination
  );

  return (
    <>
      <table className="table align-middle mb-0">
        <thead>
          <tr>
            {
              headers.map(column =>
                <th scope="col" className={classNames({ 'text-center': centeredColumns.includes(column.id) })} {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {
                    column.disableSortBy ? <></>:
                      column.isSorted
                        ? column.isSortedDesc
                          ? <i className="fas fa-fw fa-sort-down align-top ms-1" />
                          : <i className="fas fa-fw fa-sort-up align-bottom ms-1" />
                        : <i className="fas fa-fw fa-sort ms-1" />
                    
                  }
                </th>
              )
            }
          </tr>
        </thead>
        <tbody>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <td className={classNames({ 'text-center': centeredColumns.includes(cell.column.id) })} width={cell.column.width} {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>
              <div className="d-flex align-items-center">
                <label className="form-label mb-0" style={{fontSize: '14px'}}>Rows Per Page:</label>
                <select className="ms-1 form-select form-select-sm w-auto" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                  {
                    pageSizes.map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))
                  }
                </select>
                <nav>
                  <ul className="pagination pagination-sm mb-0 ms-3">
                    <li className={classNames({ 'page-item': true, 'disabled': !canPreviousPage })}>
                      <i className="page-link cursor-pointer" onClick={() => gotoPage(0)} aria-label="First">
                        <i className="fas fa-fw fa-angles-left" />
                      </i>
                    </li>
                    <li className={classNames({ 'page-item': true, 'disabled': !canPreviousPage })}>
                      <i className="page-link cursor-pointer" onClick={() => previousPage()} aria-label="Previous">
                        <i className="fas fa-fw fa-angle-left" />
                      </i>
                    </li>
                    <li className="page-item disabled">
                      <i className="page-link text-dark fw-bold">
                        {Math.min((pageIndex * pageSize) + 1, punches.length)}-{Math.min((pageIndex + 1) * pageSize, punches.length)} of {punches.length}
                      </i>
                    </li>
                    <li className={classNames({ 'page-item': true, 'disabled': !canNextPage })}>
                      <i className="page-link cursor-pointer" onClick={() => nextPage()} aria-label="Next">
                        <i className="fas fa-fw fa-angle-right" />
                      </i>
                    </li>
                    <li className={classNames({ 'page-item': true, 'disabled': !canNextPage })}>
                      <i className="page-link cursor-pointer" onClick={() => gotoPage(pageCount - 1)} aria-label="Last">
                        <i className="fas fa-fw fa-angles-right" />
                      </i>
                    </li>
                  </ul>
                </nav>
              </div>
            </td>
            <td className="fw-bold text-end text-primary"><i className='fas fa-fw fa-calculator me-2' />Total Time</td>
            <td className="fw-bold text-center text-success">{TimeUtils.totalPunchesTime(punches)}</td>
          </tr>
        </tfoot>
      </table>
    </>
  )
}

export default ReportsTable;
